import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { usePortalAuth } from '@/lib/portalAuth';
import { useBranding } from '@/lib/branding';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Heart, AlertCircle, Users, FileText, MessageSquare, Calendar } from 'lucide-react';

export default function PortalLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = usePortalAuth();
  const { organisationName } = useBranding();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await login(email, password);
      if (user.role === 'REFERRER') {
        navigate('/portal/referral');
      } else {
        navigate('/portal/dashboard');
      }
    } catch (err) {
      console.error('Portal login error:', err);
      const errorMessage = err.userMessage || 
                          err.response?.data?.error?.message || 
                          err.response?.data?.detail || 
                          'Login failed. Please check your credentials.';
      setError(errorMessage);
      setLoading(false);
      return; // Prevent finally from setting loading to false again
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-[hsl(340,70%,45%)] to-[hsl(340,55%,35%)] items-center justify-center p-12">
        <div className="relative z-10 text-white max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="h-12 w-12 rounded-xl bg-white/20 flex items-center justify-center">
              <Heart className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-3xl font-bold tracking-tight" data-testid="portal-login-org-name">{organisationName}</h1>
          </div>
          <h2 className="text-2xl font-semibold mb-4">Participant & Family Portal</h2>
          <p className="text-white/80 text-lg leading-relaxed">
            Access your care information, view upcoming supports, check your care plan, and communicate with your care team.
          </p>
          <div className="mt-12 grid grid-cols-2 gap-4">
            {[
              { icon: Calendar, label: 'View Supports' },
              { icon: FileText, label: 'Care Plan Access' },
              { icon: MessageSquare, label: 'Send Messages' },
              { icon: Users, label: 'Family Access' },
            ].map((f) => (
              <div key={f.label} className="bg-white/10 rounded-lg px-4 py-3 flex items-center gap-3">
                <f.icon className="h-5 w-5 text-white/80" />
                <span className="text-sm text-white/90">{f.label}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="absolute inset-0 bg-[radial-gradient(800px_600px_at_30%_20%,rgba(255,255,255,0.08),transparent_60%)]" />
      </div>

      {/* Right - Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-background">
        <Card className="w-full max-w-md shadow-lg border-0 bg-card">
          <CardHeader className="space-y-2 text-center pb-2">
            <div className="lg:hidden flex items-center justify-center gap-2 mb-4">
              <div className="h-10 w-10 rounded-lg bg-pink-500 flex items-center justify-center">
                <Heart className="h-5 w-5 text-white" />
              </div>
              <h1 className="text-xl font-bold" data-testid="portal-login-org-name-mobile">{organisationName} Portal</h1>
            </div>
            <CardTitle className="text-2xl">Welcome to the Portal</CardTitle>
            <CardDescription>Sign in to access your care information</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive" data-testid="portal-login-error">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <div className="space-y-2">
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="portal-login-email"
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="portal-login-password"
                  className="h-11"
                />
              </div>
              <Button
                type="submit"
                className="w-full h-11 bg-pink-500 hover:bg-pink-600"
                disabled={loading}
                data-testid="portal-login-submit"
              >
                {loading ? 'Signing in...' : 'Sign in to Portal'}
              </Button>
              <div className="text-center space-y-2">
                <Link to="/portal/forgot-password" className="block text-sm text-muted-foreground hover:text-primary">
                  Forgot password?
                </Link>
                <Link to="/login" className="block text-sm text-muted-foreground hover:text-primary">
                  Staff login
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
